/**
 * Start downloading the chunks main() will import later, without running them.
 *
 * main() imports the pixel chunks only after the consent module has loaded
 * and pmw.consent.load() has resolved, which waits up to two seconds for an
 * asynchronous CMP. Until then no pixel chunk was even requested, so the
 * downloads queued up behind the consent step. Fetching them here, as soon
 * as the public path is known, overlaps both.
 *
 * __webpack_chunk_load__() installs a chunk's module factories without
 * executing any module. The import() in main() then finds the chunk in place
 * and runs the loader at exactly the point it always did, after consent and
 * the command queue. Running the loaders early instead would register
 * listeners before queued snippets had their turn.
 *
 * The URL is built by webpack's own runtime from the same public path the
 * later import() uses, so there is never a second download of a chunk. That
 * is why this lives here and not in PHP as <link rel="preload">: a CDN
 * rewrite of the link but not of chunk_base_path, a cached page naming
 * chunks from an earlier release, or a delay-JS plugin holding back the main
 * bundle would each turn a preload hint into a wasted or duplicate download.
 *
 * A chunk that fails to download here is retried by the import() later,
 * which reports the error as before.
 *
 * Requires optimization.chunkIds "named" (gulp-webpack.js), so a chunk's id
 * is its webpackChunkName. A chunk's own async dependencies are not fetched
 * here; ip-geo, with its vendors chunk, is the only chunk that has any and it
 * is not prefetched.
 *
 * @since 1.70.1
 */

/** Pixel key in pmwDataLayer.pixels -> webpackChunkName in main.js, free and Pro builds. */
const PIXEL_CHUNKS = {
	google   : "pixel-google",
	facebook : "pixel-facebook",
	hotjar   : "pixel-hotjar",
	bing     : "pixel-microsoft",
	openai   : "pixel-openai",
	pinterest: "pixel-pinterest",
	reddit   : "pixel-reddit",
	snapchat : "pixel-snapchat",
	tiktok   : "pixel-tiktok",
}

// #if process.env.TIER === 'premium'
// /** Pixel key -> webpackChunkName for the pixels main.js imports only in the Pro build. */
// const PREMIUM_PIXEL_CHUNKS = {
// 	crazyegg     : "pixel-crazyegg",
// 	contentsquare: "pixel-contentsquare",
// 	clarity      : "pixel-clarity",
// 	triple_whale : "pixel-triple-whale",
// 	hyros        : "pixel-hyros",
// 	mixpanel     : "pixel-mixpanel",
// 	klaviyo      : "pixel-klaviyo",
// 	groundtruth  : "pixel-groundtruth",
// 	criteo       : "pixel-criteo",
// 	nextdoor     : "pixel-nextdoor",
// 	spotify      : "pixel-spotify",
// 	applovin     : "pixel-applovin",
// 	adroll       : "pixel-adroll",
// 	linkedin     : "pixel-linkedin",
// 	outbrain     : "pixel-outbrain",
// 	taboola      : "pixel-taboola",
// 	twitter      : "pixel-twitter",
// 	vwo          : "pixel-vwo",
// 	optimizely   : "pixel-optimizely",
// 	ab_tasty     : "pixel-ab-tasty",
// }
// #endif

/**
 * Names of the chunks main() is going to import on this page.
 *
 * Mirrors the conditions in main(): nothing for an excluded domain or for a
 * visitor already known to be excluded, only enabled pixels, Pro chunks only
 * when Pro features can load.
 *
 * @param {object} pmw
 * @param {object} dataLayer pmwDataLayer
 * @returns {string[]}
 */
const getChunksToPrefetch = (pmw, dataLayer) => {

	if (pmw.excludeDomainFromTracking()) return []

	const chunks = ["doofinder-listener"]

	if (window.wp?.hooks) chunks.push("wc-hooks")

	if (dataLayer?.general?.modules?.load_deprecated_functions !== false) chunks.push("deprecated-functions")

	// isVisitorAllowedSync() is idempotent; main() calls it again later.
	// null means the async IP lookup decides, and main() loads the pixels.
	if (pmw.isVisitorAllowedSync() === false) return chunks

	const pixels = dataLayer?.pixels || {}

	Object.entries(PIXEL_CHUNKS).forEach(([key, chunk]) => {
		if (pixels[key]) chunks.push(chunk)
	})

	// #if process.env.TIER === 'premium'
// 	if (pmw.canLoadPremiumFeatures()) {
// 
// 		chunks.push("pmw-premium-events")
// 
// 		Object.entries(PREMIUM_PIXEL_CHUNKS).forEach(([key, chunk]) => {
// 			if (pixels[key]) chunks.push(chunk)
// 		})
// 	}
	// #endif

	return chunks
}

/**
 * Start the downloads. Never throws and never rejects.
 *
 * @param {object} pmw
 * @param {object} dataLayer pmwDataLayer
 */
const prefetchChunks = (pmw, dataLayer) => {

	// Only the webpack build has a chunk loader (not the unit test harness)
	// eslint-disable-next-line camelcase, no-undef
	if (typeof __webpack_chunk_load__ !== "function") return

	try {
		getChunksToPrefetch(pmw, dataLayer).forEach(chunk => {
			// eslint-disable-next-line camelcase, no-undef
			__webpack_chunk_load__(chunk).catch(() => {})
		})
	} catch (error) {
		pmw.console.error("Chunk prefetch failed:", error)
	}
}

module.exports = {
	PIXEL_CHUNKS,
	// #if process.env.TIER === 'premium'
// 	PREMIUM_PIXEL_CHUNKS,
	// #endif
	getChunksToPrefetch,
	prefetchChunks,
}
